import React, { useEffect, useRef, useState } from "react";
import debounce from "lodash/debounce";
import { Modal } from "@clarkinc/zest-design-system";
import Stars from "~/common/Stars";
import { mediaReviewType } from "./Reviews";
import { User } from "~/common/Store/models/User";
import { MediaIdentifier, MediaReview, ProductReviewDetail } from "./reviewModels";
import VerifiedPurchaseBadge from "./VerifiedPurchaseBadge";
import ReviewVGVariantDetails from "./ReviewVGVariantDetails";
import ModalNavigationArrow from "./ModalNavigationArrow";
import { convertBooleanToInt, getShortenedReviewerName } from "./CustomerReviewHelper";
import MediaReviewThumbnail from "./MediaReviewThumbnail";
import BrandCarousel from "~/Brands/ExpandedFeatureGroupCarousel/BrandCarousel";
import ReviewCaption from "./ReviewCaption";
import ReviewerDetails from "./ReviewerDetails";
import ReviewImage from "./ReviewImage";
import ScrollableReviewContent from "./ScrollableReviewContent";
import ZestVideoPlayer from "~/common/videoPlayer/ZestVideoPlayer";
import(/* webpackChunkName: "modals-standalone" */"~/bootstrap/less/modals-standalone.less");
interface Props {
  productReviewDetails: ProductReviewDetail | undefined;
  brand: string;
  selectedMedia: MediaReview | undefined;
  user: User;
  handleSelectMediaElement: (newMedia: MediaIdentifier | undefined) => void;
  handleNext: () => void;
  handlePrev: () => void;
  currentReview: number;
  totalReviews: number;
  addUserUpvote: (reviewId: number, upvoted: boolean) => void;
  userVoted: boolean;
  userUpvoted: boolean;
  allMediaReviews: Array<MediaReview>;
}
const ProductReviewMediaModal = ({
  productReviewDetails,
  brand,
  selectedMedia,
  user,
  handleSelectMediaElement,
  handleNext,
  handlePrev,
  currentReview,
  totalReviews,
  addUserUpvote,
  userVoted,
  userUpvoted,
  allMediaReviews
}: Props) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState<number>(0);
  const [hideArrows, setHideArrows] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isVideo = selectedMedia?.type === mediaReviewType.video;
  const upvote = (productReviewDetails?.upvote ?? 0) + convertBooleanToInt(userUpvoted);
  const totalVotes = (productReviewDetails?.totalVotes ?? 0) + convertBooleanToInt(userVoted);
  const dateFormatted = new Date(selectedMedia?.dateSubmitted || "").toLocaleDateString(undefined, {
    month: "2-digit",
    day: "2-digit",
    year: "numeric"
  });
  const expertResponseComment = selectedMedia?.comment || productReviewDetails?.textReview?.comment || "";
  const reviewerName = getShortenedReviewerName(productReviewDetails?.fullName ?? "");
  const onOpen = () => {
    setModalOpen(true);
  };
  const onClose = () => {
    setModalOpen(false);
  };
  useEffect(() => {
    setWindowWidth(window.innerWidth);
    if (carouselRef.current) {
      if (carouselRef.current.clientWidth < 979) {
        setHideArrows(allMediaReviews.length * 80 <= carouselRef.current.clientWidth - 32);
      } else {
        setHideArrows(allMediaReviews.length * 80 <= carouselRef.current.clientWidth - 106);
      }
    }
    if (modalOpen) {
      videoRef.current?.play();
    }
    const handleResize = debounce(() => {
      setWindowWidth(window.innerWidth);
    }, 300);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [modalOpen, windowWidth, selectedMedia, allMediaReviews, videoRef]);
  return <Modal theme="wss" aria-label={`${brand} Customer Review from ${reviewerName}`} headerTitle={productReviewDetails?.productDescription ?? ""} headerProps={{
    className: "flex p-4 items-start xl:items-center",
    closeButtonProps: {
      "aria-label": "close"
    },
    closeButtonIconProps: {
      name: "ri-close-line"
    }
  }} footerProps={{
    primaryButtonProps: {
      variant: "white",
      onClick: () => Modal.toggle("zest-modal-button")
    }
  }} primaryButtonText="Close" id="zest-modal-button" className="lg:max-w-[960px] xxl:max-w-[1240px] w-full" onOpen={onOpen} onClose={onClose} contentProps={{
    className: "relative xl:px-4-1/2 lg:py-6 overflow-x-hidden md:max-h-[550px]"
  }} data-sentry-element="Modal" data-sentry-component="ProductReviewMediaModal" data-sentry-source-file="ProductReviewMediaModal.tsx">
      <div className="flex flex-col h-[calc(100%-91px)] sm:h-[calc(100%-98px)] lg:h-[calc(100%-105px)]">
        <div className="mb-[91px] sm:mb-[98px] lg:mb-[105px] h-full">
          {!!productReviewDetails && !!selectedMedia && <div className="flex items-center h-full">
              <div className="hidden md:block">
                {currentReview !== 1 ? <ModalNavigationArrow ariaLabel="view previous customer review" onClick={handlePrev} id="review-modal-prev" iconClassName="rotate-180" /> : <div className={`hidden w-9 ${allMediaReviews.length > 1 && "sm:block"}`} />}
              </div>
              <div className="flex flex-col relative xs:p-4 pb-0 pt-0 w-full h-full md:flex-row overflow-x-hidden">
                <div className={`flex basis-3/5 pt-0 border-solid border border-gray-200 rounded relative max-h-64 sm:max-h-80 md:max-h-fit xsl:min-h-[395px] ${isVideo ? "min-h-[350px]" : "min-h-[256px]"}`}>
                  <div className={`flex flex-col w-full ${isVideo && "self-center"}`}>
                    {isVideo ? <div className={`aspect-video ${selectedMedia.caption.length > 0 ? "mb-32 md:mb-20 xl:mb-16 " : ""}`}>
                        <ZestVideoPlayer posterImage={selectedMedia.thumbnail} sources={{
                    high: selectedMedia.resourcePath.substring(8)
                  }} currentIndex={0} selected={0} activePlayer={false} useCdn playsInline forwardRef={videoRef} />
                      </div> : <ReviewImage resourcePath={selectedMedia.thumbnail} caption={selectedMedia.caption} />}
                    {selectedMedia.caption.length > 0 && <ReviewCaption caption={selectedMedia.caption} windowWidth={windowWidth} />}
                  </div>
                </div>
                <div className="flex flex-col basis-2/5 pt-4 max-h-full w-full md:pt-0 md:ml-4 xxl:ml-10">
                  <Stars size="large" rating={productReviewDetails.oneToFiveStarRating} containerClasses="pb-3" />
                  <ReviewerDetails name={productReviewDetails?.fullName} company={selectedMedia.company} date={dateFormatted} />
                  <ReviewVGVariantDetails review={productReviewDetails} styles="text-gray-800 text-sm" />

                  {productReviewDetails.isVerifiedPurchase && <VerifiedPurchaseBadge />}
                  <ScrollableReviewContent addUserUpvote={addUserUpvote} expertResponseComment={expertResponseComment} reviewId={productReviewDetails.reviewId} reviewText={productReviewDetails.textReview.reviewText} totalVotes={totalVotes} upvoteCount={upvote} user={user} userId={productReviewDetails.userId} userUpvoted={userUpvoted} userVoted={userVoted} />
                </div>
              </div>
              <div className="hidden md:block">
                {currentReview !== totalReviews ? <ModalNavigationArrow ariaLabel="view next customer review" onClick={handleNext} id="review-modal-next" /> : <div className={`hidden w-9 ${allMediaReviews.length > 1 && "sm:block"}`} />}
              </div>
            </div>}
        </div>
      </div>
      <div ref={carouselRef} className="absolute w-[calc(100%-32px)] sm:w-[calc(100%-64px)] lg:w-[calc(100%-128px)] bottom-0 left-0 right-0 mx-auto py-2 sm:py-4 lg:py-6">
        <hr className="absolute top-0 border-gray-200 border-t-0 border-solid m-0 -left-8 -right-8 sm:hidden w-[120%]" style={{
        position: "inherit"
      }} />
        {modalOpen ? <BrandCarousel slideRight={({
        onClick
      }) => <ModalNavigationArrow ariaLabel="slide carousel right" onClick={onClick} id="review-carousel-next" buttonClassName={`absolute top-1/2 -translate-y-1/2 z-30 ${hideArrows && "sm:hidden"}`} showLeftSideShadow />} slideLeft={({
        onClick
      }) => <ModalNavigationArrow ariaLabel="slide carousel left" onClick={onClick} id="review-carousel-prev" iconClassName="rotate-180" buttonClassName={`absolute top-1/2 -translate-y-1/2 z-30 ${hideArrows && "sm:hidden"}`} showRightSideShadow />} listWrapperProps={{
        className: "flex mb-0 ml-0 items-start gap-2 lg:gap-4"
      }} centeredIndex={currentReview - 1}>
            {allMediaReviews.map((mediaReview, index) => <MediaReviewThumbnail key={mediaReview.id} productReviewIndex={index} mediaReview={mediaReview} setSelectedModalMedia={() => {
          handleSelectMediaElement({
            id: mediaReview.id,
            type: mediaReview.type
          });
        }} iconClassName="relative fill-current text-white" thumbnailClasses="justify-center items-center h-14 w-14" containerClasses={`m-0 ${selectedMedia?.id === mediaReview.id && "!border-2 !border-green-500 !hover:border-green-500 !p-[3px]"}`} isModalTrigger={false} />)}
          </BrandCarousel> : null}
      </div>
    </Modal>;
};
export default ProductReviewMediaModal;